import { Injectable, Injector } from '@angular/core';
import { BaseService } from '@shared/services/base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { PagedResult } from '@shared/models/paged-result';
import { Customer } from '@shared/models/customer';
import { Observable } from 'rxjs';
import { catchError, map } from '@node_modules/rxjs/operators';
import { CustomerTile } from '@shared/models/customer-tile';
import { CustomerAccountManager } from '@shared/models/customer-account-manager';

@Injectable()
export class CustomersService extends BaseService {
    constructor(private readonly _httpClient: HttpClient, protected readonly injector: Injector) {
        super(injector);
    }

    /**
     * Loads a collection of Customers based on the given criteria
     * @param id Customer Id
     * @param name Customer Name (exact match)
     * @param filter Customer Name (contains)
     * @param advisorIds Advisor Ids
     * @param accountManagerIds Account Manager Ids
     * @param sorting Sorting property and direction
     * @param maxResultCount Maximum amount of returned results
     * @param skipCount Skip amount of results
     */
    getCustomers(
        id: number | undefined,
        name: string | undefined,
        filter: string | undefined,
        advisorIds: number[] | undefined,
        accountManagerIds: number[] | undefined,
        sorting: string,
        maxResultCount: number,
        skipCount: number
    ): Observable<PagedResult<Customer>> {
        let url = this.environment.backendUrl + '/api/services/app/Customers/v2/GetCustomers';
        url = url.replace(/[?&]$/, '');

        let params = new HttpParams();

        params = params.appendAll({
            sorting: sorting,
            maxResultCount: maxResultCount,
            skipCount: skipCount,
        });

        if (id) {
            params = params.append('id', id);
        }
        if (name) {
            params = params.append('name', name);
        }
        if (filter) {
            params = params.append('filter', filter);
        }
        if (advisorIds) {
            advisorIds
                .filter((p) => p !== null && p !== undefined)
                .forEach((id: number) => {
                    params = params.append('advisorIds', id);
                });
        }
        if (accountManagerIds) {
            accountManagerIds
                .filter((p) => p !== null && p !== undefined)
                .forEach((id: number) => {
                    params = params.append('accountManagerIds', id);
                });
        }

        return this._httpClient.get<any>(url, { params: params }).pipe(
            map((response: any) => {
                return (response.result as PagedResult<Customer>) ?? null;
            }),
            catchError(this.handleError)
        );
    }

    /**
     * Loads a Customer by its Id
     * @param id Customer Id
     */
    getCustomer(id: number): Observable<Customer> {
        let url = this.environment.backendUrl + '/api/services/app/Customers/v2/GetCustomer';
        url = url.replace(/[?&]$/, '');

        let params = new HttpParams();

        params = params.appendAll({
            id: id,
        });

        return this._httpClient.get<any>(url, { params: params }).pipe(
            map((response: any) => {
                return (response.result as Customer) ?? null;
            }),
            catchError(this.handleError)
        );
    }

    /**
     * Creates a new Customer
     * @param name
     * @param address
     * @param address2
     * @param city
     * @param state
     * @param zipcode
     * @param country
     * @param billingAddress
     * @param billingAddress2
     * @param billingCity
     * @param billingState
     * @param billingZipcode
     * @param billingCountry
     * @param advisorId
     * @param accountManagerId
     */
    createCustomer(
        name: string,
        address: string,
        address2: string,
        city: string,
        state: string,
        zipcode: string,
        country: string,
        billingAddress: string,
        billingAddress2: string,
        billingCity: string,
        billingState: string,
        billingZipcode: string,
        billingCountry: string,
        advisorId: number,
        accountManagerId: number | undefined
    ): Observable<Customer> {
        let url = this.environment.backendUrl + '/api/services/app/Customers/v2/CreateCustomer';

        url = url.replace(/[?&]$/, '');

        let params = new HttpParams();

        params = params.appendAll({
            name: name,
            address: address,
            city: city,
            state: state,
            zipCode: zipcode,
            advisorId: advisorId,
        });

        if (accountManagerId) {
            params = params.append('accountManagerId', accountManagerId);
        }
        if (address2) {
            params = params.append('address2', address2);
        }
        if (country) {
            params = params.append('country', country);
        }
        if (billingAddress) {
            params = params.append('billingAddress', billingAddress);
        }
        if (billingAddress2) {
            params = params.append('billingAddress2', billingAddress2);
        }
        if (billingCity) {
            params = params.append('billingCity', billingCity);
        }
        if (billingState) {
            params = params.append('billingState', billingState);
        }
        if (billingZipcode) {
            params = params.append('billingZipcode', billingZipcode);
        }
        if (billingCountry) {
            params = params.append('billingCountry', billingCountry);
        }

        return this._httpClient.post<Customer>(url, undefined, { params: params }).pipe(
            map((response: any) => {
                return (response.result as Customer) ?? null;
            }),
            catchError(this.handleError)
        );
    }

    /**
     * Updates a Customer
     * @param id Customer Id
     * @param name
     * @param address
     * @param address2
     * @param city
     * @param state
     * @param zipcode
     * @param country
     * @param billingAddress
     * @param billingAddress2
     * @param billingCity
     * @param billingState
     * @param billingZipcode
     * @param billingCountry
     * @param advisorId
     * @param accountManagerId
     */
    updateCustomer(
        id: number,
        name: string,
        address: string,
        address2: string,
        city: string,
        state: string,
        zipcode: string,
        country: string,
        billingAddress: string,
        billingAddress2: string,
        billingCity: string,
        billingState: string,
        billingZipcode: string,
        billingCountry: string,
        advisorId: number,
        accountManagerId: number
    ): Observable<Customer> {
        let url = this.environment.backendUrl + '/api/services/app/Customers/v2/UpdateCustomer';

        url = url.replace(/[?&]$/, '');

        let params = new HttpParams();

        params = params.appendAll({
            id: id,
            name: name,
            address: address,
            city: city,
            state: state,
            zipCode: zipcode,
            advisorId: advisorId,
        });

        if (accountManagerId) {
            params = params.append('accountManagerId', accountManagerId);
        }
        if (address2) {
            params = params.append('address2', address2);
        }
        if (country) {
            params = params.append('country', country);
        }
        if (billingAddress) {
            params = params.append('billingAddress', billingAddress);
        }
        if (billingAddress2) {
            params = params.append('billingAddress2', billingAddress2);
        }
        if (billingCity) {
            params = params.append('billingCity', billingCity);
        }
        if (billingState) {
            params = params.append('billingState', billingState);
        }
        if (billingZipcode) {
            params = params.append('billingZipcode', billingZipcode);
        }
        if (billingCountry) {
            params = params.append('billingCountry', billingCountry);
        }

        return this._httpClient.put<Customer>(url, undefined, { params: params }).pipe(
            map((response: any) => {
                return (response.result as Customer) ?? null;
            }),
            catchError(this.handleError)
        );
    }

    updateCustomerAccountManager(id: number, accountManagerId: number): Observable<Customer> {
        let url = `${this.environment.backendUrl}/api/services/app/Customers/v2/${id}/account-manager`;

        let body = {
            id: id,
            accountManagerId: accountManagerId,
        };

        return this._httpClient.put<CustomerAccountManager>(url, body).pipe(
            map((response: any) => {
                return (response.result as Customer) ?? null;
            }),
            catchError(this.handleError)
        );
    }

    /**
     * Returns the Customer Tiles for the given Customer Id
     * @param customerId Customer Id
     */
    getCustomerTiles(customerId: number): Observable<any> {
        let url = this.environment.backendUrl + '/api/services/app/Customers/v2/getCustomerTiles';
        url = url.replace(/[?&]$/, '');

        let params = new HttpParams();

        params = params.append('customerId', customerId);

        return this._httpClient.get<any>(url, { params: params }).pipe(
            map((response: any) => {
                return (response.result as CustomerTile) ?? null;
            }),
            catchError(this.handleError)
        );
    }
}
